import { ReactComponent as PlayerPause } from '@assets/components/button/icPlayer-pause.svg';
import { ReactComponent as PlayerPlay } from '@assets/components/button/icPlayer-play.svg';
import { IcGaugeArrowLeft, IcGaugeArrowRight } from '@assets/icons/cameras';
import { AbsoluteCenter, ButtonGroup, Center, Icon, Text } from '@chakra-ui/react';
import { ButtonBar } from '@components/ui/buttonBar';
import PerimeterGridTitle from '@components/video/PerimeterGridTitle';
import { useAppSelector } from '@hooks/redux.hooks';
import { PerimeterCameraSelectors, selectAutomatonCabinetSegments } from '@redux/sensors/sensors.selectors';
import _, { groupBy, isEqual } from 'lodash';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

import CamGrid from './CamGrid';

const BASIC_ROTATION_INTERVAL = 15_000;

export interface PerimeterGridProps {
  uniqueKey: string;
  innerGridWidth: number;
  innerGridHeight: number;
}

function PerimeterGrid({ uniqueKey, innerGridWidth, innerGridHeight }: Readonly<PerimeterGridProps>) {
  const [zoneIndex, setZoneIndex] = useState<number>(0);
  const [chunkIndex, setChunkIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);

  const perimeterCameras: PerimeterCamera[] = useAppSelector(
    PerimeterCameraSelectors.selectAllPerimeterCameras,
    isEqual,
  );
  const segments = useAppSelector(selectAutomatonCabinetSegments, isEqual);

  const segmentByZone = useMemo(() => groupBy(segments, 'zone'), [segments]);

  const chunkedPerimeterCamerasGroupedByZone = useMemo(
    () =>
      Object.entries(segmentByZone)
        .map(([zone, seg]) => {
          const allPerimeterCamerasInZoneWithDuplicationUniqueKeys = seg.flatMap((sg) => sg.perimeterCameraUniqueKeys);
          return {
            zone: zone,
            chunkedPerimCams: _.chunk(
              perimeterCameras.filter((perimeterCamera) =>
                allPerimeterCamerasInZoneWithDuplicationUniqueKeys.includes(perimeterCamera.uniqueKey),
              ),
              innerGridHeight * innerGridWidth,
            ),
          };
        })
        .filter((zoneChunks) => zoneChunks.chunkedPerimCams.length > 0),
    [segmentByZone, perimeterCameras, innerGridHeight, innerGridWidth],
  );

  useEffect(() => {
    setZoneIndex(0);
    setChunkIndex(0);
  }, [chunkedPerimeterCamerasGroupedByZone]);

  const goPreviousChunk = () => {
    if (chunkIndex === 0) {
      setZoneIndex((index) => (index === 0 ? chunkedPerimeterCamerasGroupedByZone.length - 1 : index - 1));
      setChunkIndex(chunkedPerimeterCamerasGroupedByZone[zoneIndex - 1].chunkedPerimCams.length - 1);
    } else {
      setChunkIndex((index) => index - 1);
    }
  };

  const goNextChunk = useCallback(() => {
    if (chunkIndex === chunkedPerimeterCamerasGroupedByZone[zoneIndex].chunkedPerimCams.length - 1) {
      setZoneIndex((index) => (index === chunkedPerimeterCamerasGroupedByZone.length - 1 ? 0 : index + 1));
      setChunkIndex(0);
    } else {
      setChunkIndex((index) => index + 1);
    }
  }, [chunkIndex, chunkedPerimeterCamerasGroupedByZone, zoneIndex]);

  const camerasChunk = useMemo(() => {
    return chunkedPerimeterCamerasGroupedByZone[zoneIndex]?.chunkedPerimCams[chunkIndex] ?? [];
  }, [chunkedPerimeterCamerasGroupedByZone, zoneIndex, chunkIndex]);

  const interval = useRef<NodeJS.Timeout | undefined>(undefined);

  useLayoutEffect(() => {
    interval.current = setInterval(goNextChunk, BASIC_ROTATION_INTERVAL + 5_000 * Math.max(camerasChunk.length - 3, 0));
    return () => clearInterval(interval.current);
  }, [chunkIndex, camerasChunk, goNextChunk, zoneIndex]);

  const handlePlayButtonClick = (flag: boolean) => {
    setIsPlaying(flag);
    if (!flag) {
      clearInterval(interval.current);
      interval.current = undefined;
    } else {
      interval.current = setInterval(
        goNextChunk,
        BASIC_ROTATION_INTERVAL + 5_000 * Math.max(camerasChunk.length - 3, 0),
      );
    }
  };

  if (perimeterCameras.length === 0) {
    return (
      <Center width="100%" height="100%">
        <Text textStyle="screenLg">
          <FormattedMessage id="perimeterCameras.empty" />
        </Text>
      </Center>
    );
  }

  return (
    <>
      <PerimeterGridTitle zone={chunkedPerimeterCamerasGroupedByZone[zoneIndex]?.zone ?? ''} />
      <CamGrid
        innerGridHeight={innerGridHeight}
        innerGridWidth={innerGridWidth}
        uniqueKey={uniqueKey}
        cameras={camerasChunk}
        onPlayerDoubleClick={() => handlePlayButtonClick(false)}
      />
      <AbsoluteCenter>
        <ButtonGroup>
          <ButtonBar.Root aria-label="previous" hideTooltip onClick={goPreviousChunk}>
            <ButtonBar.Control>
              <Icon as={IcGaugeArrowLeft} />
            </ButtonBar.Control>
          </ButtonBar.Root>
          <ButtonBar.Root aria-label="player" hideTooltip onClick={() => handlePlayButtonClick(!isPlaying)}>
            <ButtonBar.Control>
              <Icon as={isPlaying ? PlayerPause : PlayerPlay} />
            </ButtonBar.Control>
          </ButtonBar.Root>
          <ButtonBar.Root aria-label="next" hideTooltip onClick={goNextChunk}>
            <ButtonBar.Control>
              <Icon as={IcGaugeArrowRight} />
            </ButtonBar.Control>
          </ButtonBar.Root>
        </ButtonGroup>
      </AbsoluteCenter>
    </>
  );
}

export default PerimeterGrid;
